import React, { useMemo } from "react"

import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import {
  getCompareByDisplayLabel,
  getCompareByGroupKeys,
  getPaletteColor,
  groupMetricOutputsByCompareBy,
  type CompareBy,
} from "@/metrics/compare-by"
import { Tooltip as CovalTooltip } from "@coval/ui"
import {
  Bar,
  BarChart,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { chartSeriesColors } from "@/components/charts/seriesPalette"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"
import { formatNumber } from "@/utils/formatting/format-number"

import { computeFullStats } from "./distributionStatsUtils"
import { ViolinPlot } from "./ViolinPlot"

const MAX_LABEL_CHARS = 35

// Pass / fail segments use the Coval Accessible Data Palette's green / red-orange
// (strong), so the result distribution reads on-palette. This intentionally
// decouples the bars from the Yes/No chip surface tokens they used to mirror.
// Unknown stays on the neutral `--coval-primary-400`.
const SUCCESS_COLOR = "#129a75" // palette green (strong)
const FAILURE_COLOR = "#bf2203" // palette red-orange (strong)
const UNKNOWN_COLOR = "var(--coval-primary-400)"

const SUCCESS_LIKE_VALUES = new Set(["YES", "PASS", "TRUE", "CORRECT"])
const FAILURE_LIKE_VALUES = new Set(["NO", "FAIL", "FALSE", "INCORRECT"])

// Semantic colors for common categorical values when the metric's success
// value is YES (the default).
const DEFAULT_CATEGORY_COLOR_MAP: Record<string, string> = {
  YES: SUCCESS_COLOR,
  PASS: SUCCESS_COLOR,
  TRUE: SUCCESS_COLOR,
  CORRECT: SUCCESS_COLOR,
  NO: FAILURE_COLOR,
  FAIL: FAILURE_COLOR,
  FALSE: FAILURE_COLOR,
  INCORRECT: FAILURE_COLOR,
  UNKNOWN: UNKNOWN_COLOR,
}

// Categorical series colors — the shared Coval Accessible Data Palette.
const CATEGORY_PALETTE = chartSeriesColors(8)

// Pick a readable inside-bar label color for a given fill. Measurable hex fills
// (the palette hues, including the pass/fail green/red) go through luminance; the
// only unmeasurable fill is the light Unknown neutral, which takes dark text.
const LIGHT_BAR_LABEL_COLOR = "#171717" // primary-900
const DARK_BAR_LABEL_COLOR = "#fff"

function getLabelColor(fill: string): string {
  if (fill === UNKNOWN_COLOR) {
    return LIGHT_BAR_LABEL_COLOR
  }
  const match = /^#([0-9a-f]{6})$/i.exec(fill)
  if (!match) {
    return DARK_BAR_LABEL_COLOR
  }
  const hex = match[1]
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  // Perceived luminance (ITU-R BT.601), normalized to 0–1.
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? LIGHT_BAR_LABEL_COLOR : DARK_BAR_LABEL_COLOR
}

function getCategoryColor(
  value: string,
  index: number,
  successValue?: string,
): string {
  const upper = value.toUpperCase()
  if (upper === ANNOTATION_VALUE.UNKNOWN) {
    return UNKNOWN_COLOR
  }
  if (successValue === ANNOTATION_VALUE.NO) {
    if (FAILURE_LIKE_VALUES.has(upper)) {
      return SUCCESS_COLOR
    }
    if (SUCCESS_LIKE_VALUES.has(upper)) {
      return FAILURE_COLOR
    }
  }
  return (
    DEFAULT_CATEGORY_COLOR_MAP[upper] ??
    CATEGORY_PALETTE[index % CATEGORY_PALETTE.length]
  )
}

function getGroupColor(
  key: string,
  compareBy: CompareBy,
  index: number,
  total: number,
): string {
  return compareBy.colors[key] ?? getPaletteColor(index, total)
}

interface Props {
  metricOutputs: RunMetricOutput[]
  runRows: RunRowLike[]
  metricRecord: {
    aggregate_units?: string | null
    unit?: string | null
  }
  compareBy: CompareBy
  successValue?: string
}

const tooltipStyle = {
  borderRadius: "6px",
  fontSize: "12px",
  padding: "6px 10px",
  backgroundColor: "var(--surface-default)",
  border: "1px solid var(--coval-border)",
  color: "var(--coval-text)",
}

const tooltipLabelStyle = {
  color: "var(--coval-text)",
}

const tooltipItemStyle = {
  color: "var(--coval-text)",
}

const axisStyle = {
  tickLine: false as const,
  axisLine: { stroke: "var(--coval-border)" },
}

// ─────────────────────────────────────────────────────────────────────────────
// Float comparison: avg bar per group + stats table
// ─────────────────────────────────────────────────────────────────────────────

function FloatComparisonChart({
  groupKeys,
  groupedOutputs,
  compareBy,
  units,
  valueMultiplier = 1,
}: {
  groupKeys: string[]
  groupedOutputs: Map<string, RunMetricOutput[]>
  compareBy: CompareBy
  units: string
  valueMultiplier?: number
}) {
  const groupStats = useMemo(() => {
    return groupKeys.map((key, i) => {
      const outputs = groupedOutputs.get(key) ?? []
      const values = outputs
        .map((mo) => mo.float_value)
        .filter((v): v is number => v !== null && Number.isFinite(v))
        .map((v) => v * valueMultiplier)
      const full = computeFullStats(values)
      return {
        key,
        label: getCompareByDisplayLabel(key, compareBy),
        color: getGroupColor(key, compareBy, i, groupKeys.length),
        count: full?.count ?? 0,
        avg: full?.mean ?? null,
        min: full?.min ?? null,
        max: full?.max ?? null,
        median: full?.median ?? null,
        stdDev: full?.stdDev ?? null,
        values,
        boxStats: full
          ? {
              q1: full.q1,
              median: full.median,
              q3: full.q3,
              whiskerLow: full.whiskerLow,
              whiskerHigh: full.whiskerHigh,
              outliers: full.outliers,
            }
          : undefined,
      }
    })
  }, [groupKeys, groupedOutputs, compareBy, valueMultiplier])

  const boxPlotGroups = groupStats
    .filter((g) => g.values.length > 0)
    .map((g) => ({
      id: g.key,
      label: g.label,
      values: g.values,
      color: g.color,
      stats: g.boxStats,
    }))

  const fmt = (v: number | null) =>
    v === null ? "—" : `${formatNumber(v)}${units ? ` ${units}` : ""}`

  return (
    <div className="w-full mt-4 space-y-5">
      {/* Stats summary table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-coval-border">
              <th className="text-left py-2 pr-4 font-medium text-coval-text-muted">
                Group
              </th>
              <th className="text-right py-2 px-3 font-medium text-coval-text-muted">
                Count
              </th>
              <th className="text-right py-2 px-3 font-medium text-coval-text-muted">
                Avg
              </th>
              <th className="text-right py-2 pl-3 font-medium text-coval-text-muted">
                Std Dev
              </th>
              <th className="text-right py-2 px-3 font-medium text-coval-text-muted">
                Median
              </th>
              <th className="text-right py-2 px-3 font-medium text-coval-text-muted">
                Min
              </th>
              <th className="text-right py-2 px-3 font-medium text-coval-text-muted">
                Max
              </th>
            </tr>
          </thead>
          <tbody>
            {groupStats.map((g) => (
              <tr
                key={g.key}
                className="border-b border-coval-border last:border-0"
              >
                <td className="py-2 pr-4">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: g.color }}
                    />
                    <CovalTooltip content={g.label}>
                      <span className="font-medium text-coval-text">
                        {g.label.length > MAX_LABEL_CHARS
                          ? `${g.label.slice(0, MAX_LABEL_CHARS)}…`
                          : g.label}
                      </span>
                    </CovalTooltip>
                  </div>
                </td>
                <td className="text-right py-2 px-3 text-coval-text-muted">
                  {g.count}
                </td>
                <td className="text-right py-2 px-3 font-medium text-coval-text">
                  {fmt(g.avg)}
                </td>
                <td className="text-right py-2 pl-3 text-coval-text-muted">
                  {fmt(g.stdDev)}
                </td>
                <td className="text-right py-2 px-3 text-coval-text-muted">
                  {fmt(g.median)}
                </td>
                <td className="text-right py-2 px-3 text-coval-text-muted">
                  {fmt(g.min)}
                </td>
                <td className="text-right py-2 px-3 text-coval-text-muted">
                  {fmt(g.max)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Boxplot */}
      {boxPlotGroups.length > 0 && (
        <div>
          <p className="text-xs text-coval-text-muted mb-2">
            Distribution by group
          </p>
          <ViolinPlot groups={boxPlotGroups} units={units} height={240} />
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Categorical comparison: stacked percentage bars
// ─────────────────────────────────────────────────────────────────────────────

const legendFormatter = (value: string) => (
  <span style={{ fontSize: 11 }}>{value}</span>
)

function CategoricalComparisonChart({
  groupKeys,
  groupedOutputs,
  compareBy,
  successValue,
}: {
  groupKeys: string[]
  groupedOutputs: Map<string, RunMetricOutput[]>
  compareBy: CompareBy
  successValue?: string
}) {
  const { allCategories, chartData } = useMemo(() => {
    const catSet = new Set<string>()
    for (const [, outputs] of groupedOutputs) {
      for (const mo of outputs) {
        if (mo.string_value) {
          catSet.add(mo.string_value.trim())
        }
      }
    }

    // Sort: success-like first, failure-like last, UNKNOWN before failure.
    // When successValue is "NO", flip success/failure ordering so the
    // success bucket still appears first in the legend/stack.
    const noIsSuccess = successValue === ANNOTATION_VALUE.NO
    const sorted = Array.from(catSet).sort((a, b) => {
      const order = (v: string) => {
        const u = v.toUpperCase()
        const isSuccessLike = noIsSuccess
          ? FAILURE_LIKE_VALUES.has(u)
          : SUCCESS_LIKE_VALUES.has(u)
        const isFailureLike = noIsSuccess
          ? SUCCESS_LIKE_VALUES.has(u)
          : FAILURE_LIKE_VALUES.has(u)
        if (isSuccessLike) {
          return 0
        }
        if (u === ANNOTATION_VALUE.UNKNOWN) {
          return 2
        }
        if (isFailureLike) {
          return 3
        }
        return 1
      }
      return order(a) - order(b)
    })

    const data = groupKeys.map((key) => {
      const outputs = groupedOutputs.get(key) ?? []
      const total = outputs.length
      const entry: Record<string, number | string> = {
        label: getCompareByDisplayLabel(key, compareBy),
        total,
      }
      for (const cat of sorted) {
        const count = outputs.filter(
          (mo) => mo.string_value?.trim() === cat,
        ).length
        entry[cat] = total > 0 ? (count / total) * 100 : 0
        entry[`${cat}__count`] = count
      }
      return entry
    })

    return { allCategories: sorted, chartData: data }
  }, [groupKeys, groupedOutputs, compareBy, successValue])

  return (
    <div className="w-full mt-4">
      <p className="text-xs text-coval-text-muted mb-2">
        Distribution by group (%)
      </p>
      <div style={{ height: Math.max(200, groupKeys.length * 52 + 60) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 8, right: 48, left: 8, bottom: 8 }}
            barCategoryGap="25%"
          >
            <XAxis
              type="number"
              domain={[0, 100]}
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 10 }}
              width={35}
              {...axisStyle}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fontSize: 11 }}
              width={120}
              {...axisStyle}
            />
            <Tooltip
              formatter={(value: number, name: string, props) => {
                const payload = (props as { payload: Record<string, number> })
                  .payload
                const count = payload[`${name}__count`]
                const total = payload.total
                return [`${value.toFixed(1)}% (${count}/${total})`, name]
              }}
              contentStyle={tooltipStyle}
              labelStyle={tooltipLabelStyle}
              itemStyle={tooltipItemStyle}
              cursor={{ fill: "var(--coval-border)" }}
            />
            <Legend
              verticalAlign="bottom"
              iconType="square"
              iconSize={10}
              formatter={legendFormatter}
            />
            {allCategories.map((cat, i) => {
              const fill = getCategoryColor(cat, i, successValue)
              return (
                <Bar
                  key={cat}
                  dataKey={cat}
                  name={cat}
                  stackId="stack"
                  fill={fill}
                  opacity={1}
                >
                  {/* Show percentage label only when segment is wide enough */}
                  <LabelList
                    dataKey={cat}
                    position="inside"
                    formatter={(v: number) =>
                      v >= 8 ? `${v.toFixed(0)}%` : ""
                    }
                    style={{
                      fontSize: 10,
                      fill: getLabelColor(fill),
                      fontWeight: 600,
                    }}
                  />
                </Bar>
              )
            })}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

export const ComparedMetricDistributionChart: React.FC<Props> = ({
  metricOutputs,
  runRows,
  metricRecord,
  compareBy,
  successValue,
}) => {
  const groupKeys = useMemo(
    () => getCompareByGroupKeys(runRows, compareBy),
    [runRows, compareBy],
  )

  const groupedOutputs = useMemo(
    () => groupMetricOutputsByCompareBy(metricOutputs, runRows, compareBy),
    [metricOutputs, runRows, compareBy],
  )

  const outputType = metricOutputs[0]?.output_type
  const units = metricRecord.aggregate_units || metricRecord.unit || ""
  const valueMultiplier = units === "%" ? 100 : 1

  if (groupKeys.length === 0) {
    return (
      <div className="text-coval-text-muted text-sm mt-4">
        No groups found for the current compare-by selection.
      </div>
    )
  }

  if (outputType === "float") {
    return (
      <FloatComparisonChart
        groupKeys={groupKeys}
        groupedOutputs={groupedOutputs}
        compareBy={compareBy}
        units={units}
        valueMultiplier={valueMultiplier}
      />
    )
  }

  if (outputType === "string") {
    return (
      <CategoricalComparisonChart
        groupKeys={groupKeys}
        groupedOutputs={groupedOutputs}
        compareBy={compareBy}
        successValue={successValue}
      />
    )
  }

  return (
    <div className="text-coval-text-muted text-sm mt-4">
      No comparison chart available for this metric type.
    </div>
  )
}
